/**
 * TenantUploadView - Public tenant upload page (multi-step)
 *
 * Flow: Welcome → Tips (3 slides) → Upload → Success
 *
 * Design tokens (from Figma):
 *   Color/brand:            #FE6F17
 *   Color/brand CTA fill:   #FE7E2F
 *   Color/brand-light:      #FFD4BF  (CTA border)
 *   Color/White:             #FCFAF5  (page bg, card bg)
 *   Color/Neutral Lightest:  #F5F4F0  (tip-item bg, illustration bg)
 *   Color/Neutral Lighter:   #EBEBE6  (tip-item border, back-btn border)
 *   Color/Neutral:           #5F6B70  (body text)
 *   Color/Neutral Darker:    #1A2023
 *   Color/Neutral Darkest:   #001116  (headings, tip-item text)
 *   Radius/Medium:           10px
 *   Radius/Large:            20px
 *   Heading:  Outfit Medium 500, 32px, lh 1.1, ls -1px
 *   Body:     Outfit Regular 400, 16px, lh 1.5
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Trash2, Upload, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { convertHeicToJpeg } from '@/services/heicConversion';
import { cn } from '@/lib/utils';

/* ─── design tokens ─── */

const C = {
  brand:           '#FE6F17',
  brandCta:        '#FE7E2F',
  brandLight:      '#FFD4BF',
  white:           '#FCFAF5',
  neutralLightest: '#F5F4F0',
  neutralLighter:  '#EBEBE6',
  neutral:         '#5F6B70',
  neutralDarkest:  '#001116',
} as const;

/* ─── types ─── */

interface TenantUploadViewProps {
  token: string;
}

interface BrandingInfo {
  logoUrl?: string;
  accentColor: string;
  companyName: string;
}

interface ProjectInfo {
  id: string;
  name: string;
  address: string;
  isUploadActive: boolean;
  branding?: BrandingInfo;
}

interface SelectedImage {
  id: string;
  file: File;
  previewUrl: string;
}

/* ─── constants ─── */

const MAX_WIDTH = 2000;
const MAX_FILE_SIZE_MB = 10;
const MAX_IMAGES = 20;
const TOTAL_STEPS = 5;

/* ─── animation variants (subtle fade-up) ─── */

const slideVariants = {
  enter: (dir: number) => ({
    y: dir > 0 ? 24 : -24,
    opacity: 0,
  }),
  center: {
    y: 0,
    opacity: 1,
  },
  exit: (dir: number) => ({
    y: dir > 0 ? -12 : 12,
    opacity: 0,
  }),
};

const slideTransition = {
  y: { type: 'tween' as const, duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const },
  opacity: { duration: 0.3 },
};

/* ─── sub-components ─── */

const Dots: React.FC<{ count: number; active: number }> = ({ count, active }) => (
  <div className="flex items-center justify-center gap-[8px]">
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        className="rounded-full transition-all duration-300"
        style={{
          width: i === active ? 8 : 6,
          height: i === active ? 8 : 6,
          backgroundColor: i === active ? C.brand : C.neutralLighter,
        }}
      />
    ))}
  </div>
);

const PoweredBy: React.FC = () => (
  <p className="text-center text-xs" style={{ color: C.neutral }}>
    Powered by <span className="font-semibold" style={{ color: C.brand }}>estage.ai</span>
  </p>
);

const TipItem: React.FC<{ emoji: string; text: string; important?: boolean }> = ({ emoji, text, important }) => (
  <div
    className="flex items-center gap-3 rounded-[10px] border px-[14px] py-[12px]"
    style={{
      backgroundColor: important ? `${C.brandLight}40` : C.neutralLightest,
      borderColor: important ? C.brandLight : C.neutralLighter,
    }}
  >
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border text-sm"
      style={{
        backgroundColor: important ? `${C.brand}20` : C.white,
        borderColor: important ? C.brandLight : C.neutralLighter,
      }}
    >
      {emoji}
    </span>
    <span className="text-[15px] font-medium" style={{ color: C.neutralDarkest }}>{text}</span>
  </div>
);

const Logo: React.FC<{ branding?: BrandingInfo }> = ({ branding }) => {
  if (branding?.logoUrl) {
    return <img src={branding.logoUrl} alt={branding.companyName} className="h-11 object-contain" />;
  }
  return <img src="/tenant/estage-logo.svg" alt="estage.ai" className="h-11 object-contain" />;
};

/* ─── main component ─── */

export const TenantUploadView: React.FC<TenantUploadViewProps> = ({ token }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /* project state */
  const [project, setProject] = useState<ProjectInfo | null>(null);
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const [projectError, setProjectError] = useState<string | null>(null);

  /* step state */
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);

  /* image state */
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [removingId, setRemovingId] = useState<string | null>(null);

  /* upload state */
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const uploadProgress = useMemo(() => {
    if (!selectedImages.length) return 0;
    return Math.round((uploadedCount / selectedImages.length) * 100);
  }, [uploadedCount, selectedImages.length]);

  useEffect(() => {
    return () => {
      selectedImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, []);

  /* ─── data fetching ─── */

  const getBrandingLogoUrl = (path: string): string => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    return `${supabaseUrl}/storage/v1/object/public/branding/${path}`;
  };

  useEffect(() => {
    const fetchProject = async () => {
      setIsLoadingProject(true);
      setProjectError(null);

      // Dev preview mode
      if (import.meta.env.DEV && token === 'preview') {
        setProject({
          id: 'preview',
          name: 'Musterstr. 72',
          address: 'Musterstr. 72, 10115 Berlin',
          isUploadActive: true,
          branding: { accentColor: C.brand, companyName: 'estage.ai' },
        });
        setIsLoadingProject(false);
        return;
      }

      try {
        let projectData: {
          id: string;
          name: string;
          address: string;
          is_upload_active: boolean;
          branding?: {
            logo_path?: string;
            accent_color?: string;
            company_name?: string;
          };
        } | null = null;

        const { data: brandingRpcData, error: brandingRpcError } = await supabase.rpc(
          'get_tenant_upload_branding',
          { input_token: token },
        );

        if (brandingRpcError) {
          const errMsg = brandingRpcError.message?.toLowerCase() || '';
          if (errMsg.includes('function') && errMsg.includes('does not exist')) {
            const { data: rpcData, error: rpcError } = await supabase.rpc('get_tenant_project_info', {
              input_token: token,
            });
            if (rpcError) {
              const rpcErrMsg = rpcError.message?.toLowerCase() || '';
              if (rpcErrMsg.includes('function') && rpcErrMsg.includes('does not exist')) {
                const { data: directData, error: directError } = await supabase
                  .from('projects')
                  .select('id, name, street, is_upload_active')
                  .eq('upload_token', token)
                  .maybeSingle();
                if (directError) throw new Error(directError.message);
                if (directData) {
                  projectData = {
                    id: directData.id,
                    name: directData.name,
                    address: directData.street,
                    is_upload_active: directData.is_upload_active ?? true,
                  };
                }
              } else {
                throw new Error(rpcError.message);
              }
            } else if (rpcData) {
              projectData = {
                id: rpcData.id,
                name: rpcData.name,
                address: rpcData.address,
                is_upload_active: rpcData.is_upload_active ?? true,
              };
            }
          } else {
            throw new Error(brandingRpcError.message);
          }
        } else if (brandingRpcData) {
          projectData = {
            id: brandingRpcData.id,
            name: brandingRpcData.name,
            address: brandingRpcData.address,
            is_upload_active: brandingRpcData.is_upload_active ?? true,
            branding: brandingRpcData.branding,
          };
        }

        if (!projectData?.id) {
          setProjectError('Ungültiger oder abgelaufener Upload-Link.');
          setProject(null);
          return;
        }

        const branding: BrandingInfo = {
          accentColor: projectData.branding?.accent_color || C.brand,
          companyName: projectData.branding?.company_name || 'estage.ai',
          logoUrl: projectData.branding?.logo_path
            ? getBrandingLogoUrl(projectData.branding.logo_path)
            : undefined,
        };

        setProject({
          id: projectData.id,
          name: projectData.name,
          address: projectData.address,
          isUploadActive: projectData.is_upload_active,
          branding,
        });
      } catch (err) {
        setProjectError(err instanceof Error ? err.message : 'Projekt konnte nicht geladen werden.');
        setProject(null);
      } finally {
        setIsLoadingProject(false);
      }
    };

    fetchProject();
  }, [token]);

  /* ─── image handling ─── */

  const compressImage = useCallback(async (file: File): Promise<File> => {
    try {
      const imageUrl = URL.createObjectURL(file);
      const image = new Image();
      const loadPromise = new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Image load failed'));
        setTimeout(() => reject(new Error('Image load timeout')), 10000);
      });
      image.src = imageUrl;
      try {
        await loadPromise;
      } catch {
        URL.revokeObjectURL(imageUrl);
        return file;
      }
      const scale = Math.min(1, MAX_WIDTH / image.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      const context = canvas.getContext('2d');
      if (!context) {
        URL.revokeObjectURL(imageUrl);
        return file;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(imageUrl);
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.85);
      });
      if (!blob) return file;
      const fileName = file.name.replace(/\.[^/.]+$/, '.jpg');
      return new File([blob], fileName, { type: 'image/jpeg' });
    } catch {
      return file;
    }
  }, []);

  const uploadSingleFile = useCallback(
    async (file: File) => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseAnonKey) throw new Error('Supabase Konfiguration fehlt.');
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) throw new Error(`Datei ist größer als ${MAX_FILE_SIZE_MB}MB.`);
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append('token', token);
      formData.append('file', compressed, compressed.name);
      const response = await fetch(`${supabaseUrl}/functions/v1/upload-tenant-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${supabaseAnonKey}` },
        body: formData,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Upload fehlgeschlagen.');
    },
    [compressImage, token],
  );

  const isImageFile = useCallback((file: File): boolean => {
    if (file.type.startsWith('image/')) return true;
    const ext = file.name.toLowerCase().split('.').pop();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'tiff', 'tif'];
    if (ext && imageExtensions.includes(ext)) return true;
    if (!file.type && file.size > 0) return true;
    return false;
  }, []);

  const handleAddFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!project?.isUploadActive) {
        setProjectError('Der Upload-Link ist deaktiviert.');
        return;
      }
      const imageFiles = Array.from(files).filter((file) => isImageFile(file));
      if (imageFiles.length === 0) return;
      const remainingSlots = MAX_IMAGES - selectedImages.length;
      const filesToAdd = imageFiles.slice(0, remainingSlots);
      if (filesToAdd.length < imageFiles.length) {
        setUploadErrors([`Maximal ${MAX_IMAGES} Bilder erlaubt. ${imageFiles.length - filesToAdd.length} Bilder wurden ignoriert.`]);
      }
      const convertedFiles: File[] = [];
      for (const file of filesToAdd) {
        try {
          const converted = await convertHeicToJpeg(file);
          convertedFiles.push(converted);
        } catch {
          convertedFiles.push(file);
        }
      }
      const newImages: SelectedImage[] = convertedFiles.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      setSelectedImages((prev) => [...prev, ...newImages]);
      setIsComplete(false);
      setUploadErrors([]);
    },
    [project?.isUploadActive, selectedImages.length, isImageFile],
  );

  const handleRemoveImage = useCallback((imageId: string) => {
    setRemovingId(imageId);
    setTimeout(() => {
      setSelectedImages((prev) => {
        const toRemove = prev.find((img) => img.id === imageId);
        if (toRemove) URL.revokeObjectURL(toRemove.previewUrl);
        return prev.filter((img) => img.id !== imageId);
      });
      setRemovingId(null);
    }, 200);
  }, []);

  const handleClearAll = useCallback(() => {
    selectedImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    setSelectedImages([]);
    setUploadErrors([]);
  }, [selectedImages]);

  const handleUploadAll = useCallback(async () => {
    if (selectedImages.length === 0) return;
    setIsUploading(true);
    setUploadedCount(0);
    setUploadErrors([]);
    const errors: string[] = [];
    for (const image of selectedImages) {
      try {
        await uploadSingleFile(image.file);
        setUploadedCount((prev) => prev + 1);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload fehlgeschlagen.';
        errors.push(`${image.file.name}: ${message}`);
      }
    }
    setUploadErrors(errors);
    setIsUploading(false);
    if (errors.length === 0) {
      selectedImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      setSelectedImages([]);
      setIsComplete(true);
    }
  }, [selectedImages, uploadSingleFile]);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      if (event.dataTransfer.files?.length) handleAddFiles(event.dataTransfer.files);
    },
    [handleAddFiles],
  );

  /* ─── navigation ─── */

  const goNext = useCallback(() => {
    setDirection(1);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  }, []);

  const goBack = useCallback(() => {
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 0));
  }, []);

  const resetToUpload = useCallback(() => {
    setIsComplete(false);
    setStep(4);
    setDirection(1);
  }, []);

  const canAddImages = !isLoadingProject && project?.isUploadActive && !isUploading && selectedImages.length < MAX_IMAGES;

  /* ─── shared UI pieces ─── */

  const backButton = (
    <button
      type="button"
      onClick={goBack}
      className="flex h-11 w-[45px] shrink-0 items-center justify-center rounded-[10px] font-medium text-lg transition-colors hover:bg-[#F5F4F0]"
      style={{ backgroundColor: C.white, border: `1.5px solid ${C.neutralLighter}`, color: C.neutralDarkest }}
    >
      &larr;
    </button>
  );

  const ctaButton = (label: string, onClick: () => void, icon?: React.ReactNode, disabled?: boolean) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-[10px] border font-medium text-base text-white transition-colors active:scale-[0.98] disabled:opacity-40"
      style={{ backgroundColor: C.brandCta, borderColor: C.brandLight }}
    >
      {label}
      {icon}
    </button>
  );

  const renderNavigation = (showBack: boolean, label: string, onClick: () => void) => (
    <div className="flex items-center gap-[10px]">
      {showBack && backButton}
      {ctaButton(label, onClick, <span className="text-sm">&rarr;</span>)}
    </div>
  );

  /* heading style: Outfit Medium, 32px, lh 1.1, ls -1px */
  const headingStyle = 'text-[32px] font-medium leading-[1.1] tracking-[-1px]';

  /* ─── slide content ─── */

  const renderStep = () => {
    /* Step 0: Welcome */
    if (step === 0) {
      return (
        <div className="flex min-h-full flex-col">
          {/* Illustration — edge to edge, top-aligned */}
          <div
            className="relative -mx-8 -mt-8 w-[calc(100%+64px)] overflow-hidden"
            style={{ backgroundColor: C.neutralLightest }}
          >
            <img
              src="/tenant/EB_IMG_Illustration_Foto-Machen.webp"
              alt="Foto machen"
              className="mx-auto block h-auto w-full object-contain object-top"
              draggable={false}
            />
          </div>

          {/* Logo overlay */}
          <div className="pointer-events-none absolute left-0 right-0 top-[32px] flex items-center justify-center">
            <Logo branding={project?.branding} />
          </div>

          <div className="flex flex-1 flex-col px-0 pt-8">
            <h1 className={headingStyle} style={{ color: C.neutralDarkest }}>
              Bilder für{' '}
              <span style={{ color: C.brand }}>{project?.name || 'dein Projekt'}</span>{' '}
              hochladen.
            </h1>
            <p className="mt-3 text-base leading-relaxed" style={{ color: C.neutral }}>
              Du wurdest eingeladen, Bilder deiner Räume hochzuladen.
            </p>

            <div className="mt-auto space-y-5 pb-2 pt-8">
              <Dots count={TOTAL_STEPS} active={0} />
              <div className="flex items-center gap-[10px]">
                {ctaButton("Los geht's", goNext, <span className="text-sm">&rarr;</span>)}
              </div>
              <PoweredBy />
            </div>
          </div>
        </div>
      );
    }

    /* image container height for slides 2-4 (~40% of card) */
    const imgH = 'h-[340px]';

    /* Step 1: Raum aufräumen */
    if (step === 1) {
      return (
        <div className="flex min-h-full flex-col">
          <div
            className={cn('relative -mt-8 flex items-center justify-center overflow-hidden rounded-b-[20px]', imgH)}
            style={{ backgroundColor: C.neutralLightest }}
          >
            <img
              src="/tenant/EB_SYMB_Putzen.webp"
              alt="Raum aufräumen"
              className="block h-auto max-h-[90%] w-auto object-contain"
              draggable={false}
            />
          </div>

          <div className="flex flex-1 flex-col pt-10">
            <h2 className={headingStyle} style={{ color: C.neutralDarkest }}>
              Raum grob aufräumen
            </h2>

            <div className="mt-6 space-y-3">
              <TipItem emoji="👕" text="Lose Gegenstände wegräumen" />
              <TipItem emoji="🗑" text="Müll & Flaschen entfernen" />
              <TipItem emoji="🛋️" text="Möbel können bleiben" />
            </div>

            <div className="mt-auto space-y-5 pb-2 pt-8">
              <Dots count={TOTAL_STEPS} active={1} />
              {renderNavigation(true, 'Weiter', goNext)}
              <PoweredBy />
            </div>
          </div>
        </div>
      );
    }

    /* Step 2: Bitte darauf achten */
    if (step === 2) {
      return (
        <div className="flex min-h-full flex-col">
          <div
            className={cn('relative -mt-8 flex items-center justify-center overflow-hidden rounded-b-[20px]', imgH)}
            style={{ backgroundColor: C.neutralLightest }}
          >
            <img
              src="/tenant/EB_IMG_Illustration_Vorhang.webp"
              alt="Vorhänge öffnen"
              className="block h-auto max-h-full w-auto object-contain"
              draggable={false}
            />
          </div>

          <div className="flex flex-1 flex-col pt-6">
            <span
              className="mb-2 inline-flex w-fit items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white"
              style={{ backgroundColor: C.brand }}
            >
              ⚡ Sehr wichtig
            </span>
            <h2 className={headingStyle} style={{ color: C.neutralDarkest }}>
              Bitte darauf achten:
            </h2>

            <div className="mt-5 space-y-3">
              <TipItem emoji="☀️" text="Für ausreichende Helligkeit sorgen" important />
              <TipItem emoji="🪟" text="Vorhänge & Rollos komplett öffnen" important />
              <TipItem emoji="🚪" text="Türen zu anderen Räumen schließen" important />
            </div>

            <div className="mt-auto space-y-5 pb-2 pt-8">
              <Dots count={TOTAL_STEPS} active={2} />
              {renderNavigation(true, 'Weiter', goNext)}
              <PoweredBy />
            </div>
          </div>
        </div>
      );
    }

    /* Step 3: So wird's perfekt */
    if (step === 3) {
      return (
        <div className="flex min-h-full flex-col">
          <div
            className={cn('relative -mt-8 flex items-center justify-center overflow-hidden rounded-b-[20px]', imgH)}
            style={{ backgroundColor: C.neutralLightest }}
          >
            <img
              src="/tenant/EB_SYMB_Foto.webp"
              alt="Foto Tipps"
              className="block h-auto max-h-[90%] w-auto object-contain"
              draggable={false}
            />
          </div>

          <div className="flex flex-1 flex-col pt-10">
            <h2 className={headingStyle} style={{ color: C.neutralDarkest }}>
              So wird&apos;s perfekt:
            </h2>

            <div className="mt-6 space-y-3">
              <TipItem emoji="📱" text="Smartphone quer halten" />
              <TipItem emoji="📐" text="Aus der Ecke fotografieren" />
            </div>

            <div className="mt-auto space-y-5 pb-2 pt-8">
              <Dots count={TOTAL_STEPS} active={3} />
              {renderNavigation(true, 'Weiter', goNext)}
              <PoweredBy />
            </div>
          </div>
        </div>
      );
    }

    /* Step 4: Bilder hochladen */
    if (step === 4) {
      return (
        <div className="flex min-h-full flex-col pt-16">
          <h2 className={headingStyle} style={{ color: C.neutralDarkest }}>
            Bilder hochladen
          </h2>

          <div className="mt-6 flex flex-1 flex-col">
            {/* Upload zone */}
            <label
              htmlFor="tenant-file-input"
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-4 transition-colors',
                selectedImages.length > 0 ? 'py-5' : 'py-10',
              )}
              style={{ borderColor: C.brandLight, backgroundColor: `${C.brandLight}30` }}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <img
                src="/tenant/EB_SYMB_upload.webp"
                alt="Upload"
                className={cn('object-contain', selectedImages.length > 0 ? 'h-16' : 'h-20')}
                draggable={false}
              />
              <div className="text-center">
                <p className="text-[15px] font-medium" style={{ color: C.neutralDarkest }}>Fotos auswählen</p>
                <p className="mt-1 text-[13px]" style={{ color: C.neutral }}>Tippen oder Dateien hierher ziehen</p>
                <p className="mt-0.5 text-[11px]" style={{ color: C.neutralLighter }}>JPEG &middot; PNG &middot; HEIC &middot; max. 20 MB</p>
              </div>
            </label>

            {/* Thumbnails */}
            {selectedImages.length > 0 && (
              <div className="mt-4 space-y-3">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {selectedImages.map((image) => (
                    <div
                      key={image.id}
                      className={cn(
                        'group relative h-[79px] w-[79px] shrink-0 overflow-hidden rounded-lg transition-all duration-200',
                        removingId === image.id && 'scale-90 opacity-0',
                      )}
                    >
                      <img src={image.previewUrl} alt={image.file.name} className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(image.id)}
                        disabled={isUploading}
                        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {canAddImages && (
                    <label
                      htmlFor="tenant-file-input"
                      className="flex h-[79px] w-[79px] shrink-0 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-lg border-2 border-dashed transition-colors"
                      style={{ borderColor: C.neutralLighter, color: C.neutral }}
                    >
                      <Plus className="h-5 w-5" />
                      <span className="text-[10px]">Mehr</span>
                    </label>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleClearAll}
                  disabled={isUploading}
                  className="flex items-center gap-1.5 text-xs transition-colors"
                  style={{ color: C.neutral }}
                >
                  <Trash2 className="h-3 w-3" />
                  Alle entfernen
                </button>
              </div>
            )}

            {/* Upload progress */}
            {isUploading && (
              <div className="mt-4 space-y-2">
                <div className="h-2 overflow-hidden rounded-full" style={{ backgroundColor: C.neutralLightest }}>
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%`, backgroundColor: C.brand }}
                  />
                </div>
                <p className="text-center text-xs" style={{ color: C.neutral }}>
                  {uploadedCount} / {selectedImages.length} hochgeladen…
                </p>
              </div>
            )}

            {/* Errors */}
            {uploadErrors.length > 0 && (
              <div className="mt-3 rounded-[10px] border border-red-200 bg-red-50 p-3 text-xs text-red-600">
                {uploadErrors.map((error, idx) => (
                  <p key={idx}>{error}</p>
                ))}
              </div>
            )}

            {/* Hidden file input */}
            <input
              id="tenant-file-input"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              disabled={!canAddImages}
              onChange={(event) => {
                const files = event.target.files;
                if (files && files.length > 0) handleAddFiles(files);
                event.target.value = '';
              }}
            />

            <div className="mt-auto space-y-5 pb-2 pt-8">
              <Dots count={TOTAL_STEPS} active={4} />
              <div className="flex items-center gap-[10px]">
                {backButton}
                {selectedImages.length > 0 ? (
                  <button
                    type="button"
                    onClick={handleUploadAll}
                    disabled={isUploading}
                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] border font-medium text-base text-white transition-colors active:scale-[0.98] disabled:opacity-60"
                    style={{ backgroundColor: C.brandCta, borderColor: C.brandLight }}
                  >
                    Hochladen
                    <Upload className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] border font-medium text-base text-white opacity-40"
                    style={{ backgroundColor: C.brandCta, borderColor: C.brandLight }}
                  >
                    Weiter <span className="text-sm">&rarr;</span>
                  </button>
                )}
              </div>
              <PoweredBy />
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  /* ─── success screen ─── */

  if (isComplete) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center sm:px-4" style={{ backgroundColor: C.white }}>
        <div
          className="relative mx-auto flex w-full max-w-[393px] flex-col overflow-hidden sm:rounded-3xl sm:shadow-xl"
          style={{ backgroundColor: C.white, height: '100dvh', maxHeight: 852 }}
        >
          {/* Top area with logo + illustration */}
          <div className="relative px-8 pb-0 pt-10" style={{ backgroundColor: C.neutralLightest }}>
            <div className="flex items-center justify-center pb-6">
              <Logo branding={project?.branding} />
            </div>
            <img
              src="/tenant/EB_SYMB_Rock.webp"
              alt="Geschafft!"
              className="mx-auto block h-auto w-[65%] object-contain"
              draggable={false}
            />
          </div>

          <div className="flex flex-1 flex-col px-8 pt-8">
            <h2 className={headingStyle}>
              <span style={{ color: C.brand }}>Geschafft!</span>
              <br />
              <span style={{ color: C.neutralDarkest }}>Vielen Dank!</span>
            </h2>
            <p className="mt-3 text-base leading-relaxed" style={{ color: C.neutral }}>
              Die Bilder wurden an den Makler übermittelt.
            </p>

            <div className="mt-auto space-y-5 pb-8 pt-10">
              <button
                type="button"
                onClick={resetToUpload}
                className="flex h-12 w-full items-center justify-center rounded-[10px] border-[1.5px] font-medium transition-colors"
                style={{ backgroundColor: C.white, borderColor: C.neutralLighter, color: C.neutralDarkest }}
              >
                Weitere Bilder hochladen
              </button>
              <PoweredBy />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ─── loading / error states ─── */

  if (isLoadingProject) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center" style={{ backgroundColor: C.white }}>
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: C.brand, borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: C.neutral }}>Projekt wird geladen…</p>
        </div>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center px-4" style={{ backgroundColor: C.white }}>
        <div className="mx-auto max-w-sm rounded-2xl p-8 text-center shadow-lg" style={{ backgroundColor: '#fff' }}>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
            <X className="h-6 w-6 text-red-500" />
          </div>
          <h2 className="text-lg font-medium" style={{ color: C.neutralDarkest }}>Link ungültig</h2>
          <p className="mt-2 text-sm" style={{ color: C.neutral }}>
            {projectError || 'Der Upload-Link ist ungültig oder abgelaufen.'}
          </p>
        </div>
      </div>
    );
  }

  /* ─── main layout ─── */

  return (
    <div className="flex min-h-[100dvh] items-center justify-center sm:px-4 sm:py-6" style={{ backgroundColor: C.white }}>
      <div
        className="relative mx-auto w-full max-w-[393px] overflow-hidden sm:rounded-3xl sm:shadow-xl"
        style={{ backgroundColor: C.white, height: '100dvh', maxHeight: 852 }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="absolute inset-0 overflow-y-auto overflow-x-hidden p-8"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
